import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Server,
  Activity,
  Clock,
  TrendingUp,
  TrendingDown,
  ArrowUpRight,
  MoreHorizontal,
  AlertCircle,
  DollarSign,
  Cpu,
  MemoryStick,
  HardDrive,
  Monitor,
  Database,
  CheckCircle,
  XCircle,
  Pause,
  Play,
  BarChart3,
  Zap,
  Shield,
  Globe,
  Sparkles,
  Star,
  Calendar,
  User,
  ChevronRight,
  Eye,
  Download,
  Upload
} from 'lucide-react';
import { 
  mockSystemStats, 
  mockJobs, 
  mockUser,
  mockWorkstations,
  mockQuotas,
  mockBillRecords 
} from '../../../mock/data';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  // 直接使用Mock数据，避免Hook的复杂性
  const systemStats = mockSystemStats;
  const jobsData = mockJobs.slice(0, 4); // 只取前4个作业
  const userInfo = mockUser;
  const workstations = mockWorkstations;
  const quotas = mockQuotas;
  const billRecords = mockBillRecords.slice(0, 3); // 最近3条账单记录

  // 计算统计数据
  const stats = [
    { 
      title: '运行中作业', 
      value: systemStats.runningJobs.toString(), 
      change: '+12%', 
      trend: 'up', 
      icon: Play,
      color: 'text-green-600'
    },
    { 
      title: '总作业数', 
      value: systemStats.totalJobs.toString(), 
      change: '+5%', 
      trend: 'up', 
      icon: Database,
      color: 'text-blue-600'
    },
    { 
      title: '活跃用户', 
      value: systemStats.activeUsers.toString(), 
      change: '+8%', 
      trend: 'up', 
      icon: Users,
      color: 'text-purple-600'
    },
    { 
      title: '系统负载', 
      value: `${Math.round(systemStats.systemLoad * 100)}%`, 
      change: '-2%', 
      trend: 'down', 
      icon: Activity,
      color: 'text-orange-600'
    }
  ];

  // 资源使用情况 (模拟数据)
  const resourceUsage = [
    { name: 'CPU', value: Math.round(systemStats.systemLoad * 100), color: 'bg-blue-500' },
    { name: '内存', value: 75, color: 'bg-green-500' },
    { name: '存储', value: 60, color: 'bg-yellow-500' },
    { name: 'GPU', value: 85, color: 'bg-purple-500' }
  ];

  // 最近作业
  const recentJobs = jobsData.map(job => ({
    id: job.id,
    name: job.name,
    status: job.status,
    user: userInfo.username || '用户',
    time: new Date(job.createdAt).toLocaleString()
  }));

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'running': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'failed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'running': return '运行中';
      case 'completed': return '已完成';
      case 'pending': return '等待中';
      case 'failed': return '失败';
      case 'cancelled': return '已取消';
      default: return '未知';
    }
  };

  return (
    <div className="min-h-full bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      {/* 装饰性背景 */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-32 w-96 h-96 bg-gradient-to-br from-blue-400/20 to-purple-600/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-32 -left-40 w-80 h-80 bg-gradient-to-tr from-indigo-400/20 to-cyan-600/20 rounded-full blur-3xl"></div>
      </div>

      <div className="relative space-y-6 portrait:space-y-4">
        {/* Enhanced Page Header */}
        <div className="bg-white/70 backdrop-blur-xl rounded-3xl p-8 portrait:p-4 portrait:md:p-6 border border-white/20 shadow-xl portrait:rounded-2xl">
          <div className="flex items-center justify-between portrait:flex-col portrait:space-y-4 portrait:items-start">
            <div className="flex items-center space-x-4 portrait:space-x-3">
              <div className="w-16 h-16 portrait:w-12 portrait:h-12 portrait:md:w-14 portrait:md:h-14 bg-gradient-to-br from-blue-500 via-purple-600 to-indigo-700 rounded-2xl portrait:rounded-xl flex items-center justify-center shadow-lg">
                <BarChart3 className="w-8 h-8 portrait:w-6 portrait:h-6 portrait:md:w-7 portrait:md:h-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl portrait:text-xl portrait:md:text-2xl font-bold bg-gradient-to-r from-gray-800 via-blue-800 to-purple-800 bg-clip-text text-transparent">
                  智能仪表盘
                </h1>
                <p className="text-gray-600 mt-1 portrait:text-sm portrait:md:text-base">实时监控您的云超算资源，AI驱动的智能分析</p>
              </div>
            </div>
            <div className="flex items-center space-x-4 portrait:space-x-2 portrait:flex-wrap portrait:w-full portrait:justify-start">
              <div className="flex items-center space-x-2 text-sm portrait:text-xs text-gray-500 bg-white/50 rounded-xl px-4 py-2 portrait:px-3 portrait:py-1.5">
                <Activity className="w-4 h-4 portrait:w-3 portrait:h-3 text-green-500 animate-pulse" />
                <span>实时监控</span>
              </div>
              <div className="flex items-center space-x-2 text-sm portrait:text-xs text-gray-500 bg-white/50 rounded-xl px-4 py-2 portrait:px-3 portrait:py-1.5">
                <Clock className="w-4 h-4 portrait:w-3 portrait:h-3" />
                <span className="portrait:hidden">最后更新: {new Date().toLocaleTimeString()}</span>
                <span className="hidden portrait:inline">更新: {new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Enhanced Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 portrait:grid-cols-2 portrait:lg:grid-cols-4 gap-6 portrait:gap-3 portrait:md:gap-4">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            const isPositive = stat.trend === 'up';
            return (
              <div key={index} className="group relative">
                {/* 装饰性背景 */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 to-purple-600/10 rounded-2xl portrait:rounded-xl blur group-hover:blur-lg transition-all duration-300"></div>
                
                <div className="relative bg-white/80 backdrop-blur-xl rounded-2xl portrait:rounded-xl p-6 portrait:p-4 border border-white/30 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                  <div className="flex items-center justify-between mb-4 portrait:mb-3">
                    <div className={`w-12 h-12 portrait:w-10 portrait:h-10 rounded-xl portrait:rounded-lg flex items-center justify-center shadow-lg ${
                      index === 0 ? 'bg-gradient-to-br from-green-400 to-emerald-600' :
                      index === 1 ? 'bg-gradient-to-br from-blue-400 to-cyan-600' :
                      index === 2 ? 'bg-gradient-to-br from-purple-400 to-pink-600' :
                      'bg-gradient-to-br from-orange-400 to-red-600'
                    }`}>
                      <Icon className="w-6 h-6 portrait:w-5 portrait:h-5 text-white" />
                    </div>
                    <div className={`flex items-center space-x-1 text-sm portrait:text-xs font-medium px-2 py-1 portrait:px-1.5 portrait:py-0.5 rounded-lg ${
                      isPositive ? 'text-green-700 bg-green-100' : 'text-red-700 bg-red-100'
                    }`}>
                      {isPositive ? <TrendingUp className="w-4 h-4 portrait:w-3 portrait:h-3" /> : <TrendingDown className="w-4 h-4 portrait:w-3 portrait:h-3" />}
                      <span>{stat.change}</span>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm portrait:text-xs font-medium text-gray-600 mb-1">{stat.title}</h3>
                    <p className="text-3xl portrait:text-2xl font-bold text-gray-900">{stat.value}</p>
                  </div>
                  
                  {/* 装饰性光效 */}
                  <div className="absolute top-2 right-2 w-2 h-2 portrait:w-1.5 portrait:h-1.5 bg-gradient-to-r from-blue-400 to-purple-500 rounded-full animate-pulse opacity-70"></div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 portrait:grid-cols-1 gap-8 portrait:gap-4">
          {/* Enhanced Resource Usage */}
          <div className="bg-white/80 backdrop-blur-xl rounded-2xl portrait:rounded-xl p-8 portrait:p-4 portrait:md:p-6 border border-white/30 shadow-xl">
            <div className="flex items-center justify-between mb-6 portrait:mb-4">
              <div className="flex items-center space-x-3 portrait:space-x-2">
                <div className="w-10 h-10 portrait:w-8 portrait:h-8 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl portrait:rounded-lg flex items-center justify-center">
                  <Monitor className="w-5 h-5 portrait:w-4 portrait:h-4 text-white" />
                </div>
                <h2 className="text-xl portrait:text-lg font-bold text-gray-900">资源使用情况</h2>
              </div>
              <button className="text-blue-600 hover:text-blue-700 flex items-center space-x-1 text-sm portrait:text-xs font-medium">
                <Eye className="w-4 h-4 portrait:w-3 portrait:h-3" />
                <span>查看详情</span>
              </button>
            </div>
            
            <div className="space-y-6 portrait:space-y-4">
              {resourceUsage.map((resource, index) => (
                <div key={index} className="group">
                  <div className="flex items-center justify-between mb-3 portrait:mb-2">
                    <span className="text-sm portrait:text-xs font-semibold text-gray-700">{resource.name}</span>
                    <span className="text-sm portrait:text-xs font-bold text-gray-900">{resource.value}%</span>
                  </div>
                  <div className="relative">
                    <div className="w-full bg-gray-100 rounded-full h-3 portrait:h-2 shadow-inner">
                      <div 
                        className={`h-3 portrait:h-2 rounded-full transition-all duration-1000 ease-out shadow-sm ${resource.color}`}
                        style={{ width: `${resource.value}%` }}
                      ></div>
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-r from-white/30 to-transparent rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Enhanced Recent Jobs */}
          <div className="bg-white/80 backdrop-blur-xl rounded-2xl portrait:rounded-xl p-8 portrait:p-4 portrait:md:p-6 border border-white/30 shadow-xl">
            <div className="flex items-center justify-between mb-6 portrait:mb-4">
              <div className="flex items-center space-x-3 portrait:space-x-2">
                <div className="w-10 h-10 portrait:w-8 portrait:h-8 bg-gradient-to-br from-green-500 to-teal-600 rounded-xl portrait:rounded-lg flex items-center justify-center">
                  <Activity className="w-5 h-5 portrait:w-4 portrait:h-4 text-white" />
                </div>
                <h2 className="text-xl portrait:text-lg font-bold text-gray-900">最近作业</h2>
              </div>
              <button 
                onClick={() => navigate('/console#/jobManagement')}
                className="text-blue-600 hover:text-blue-700 flex items-center space-x-1 text-sm portrait:text-xs font-medium"
              >
                <span>查看全部</span>
                <ChevronRight className="w-4 h-4 portrait:w-3 portrait:h-3" />
              </button>
            </div>
            
            <div className="space-y-4 portrait:space-y-3">
              {recentJobs.map((job) => (
                <div key={job.id} className="group flex items-center justify-between p-4 portrait:p-3 rounded-xl portrait:rounded-lg hover:bg-white/60 transition-all duration-300 border border-gray-100/50">
                  <div className="flex items-center space-x-4 portrait:space-x-3">
                    <div className="w-8 h-8 portrait:w-6 portrait:h-6 bg-gradient-to-br from-blue-400 to-indigo-600 rounded-lg portrait:rounded-md flex items-center justify-center text-white text-xs portrait:text-[10px] font-bold">
                      {job.name.charAt(0)}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors text-sm portrait:text-xs">{job.name}</h3>
                      <p className="text-xs portrait:text-[10px] text-gray-500">{job.time}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-3 py-1 portrait:px-2 portrait:py-0.5 rounded-full text-xs portrait:text-[10px] font-semibold ${getStatusColor(job.status)}`}>
                      {getStatusText(job.status)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Enhanced Quick Actions */}
        <div className="bg-white/80 backdrop-blur-xl rounded-2xl portrait:rounded-xl p-8 portrait:p-4 portrait:md:p-6 border border-white/30 shadow-xl">
          <div className="flex items-center space-x-3 portrait:space-x-2 mb-6 portrait:mb-4">
            <div className="w-10 h-10 portrait:w-8 portrait:h-8 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl portrait:rounded-lg flex items-center justify-center">
              <Zap className="w-5 h-5 portrait:w-4 portrait:h-4 text-white" />
            </div>
            <h2 className="text-xl portrait:text-lg font-bold text-gray-900">快速操作</h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 portrait:grid-cols-2 gap-4 portrait:gap-3">
            <button 
              onClick={() => navigate('/console#/submit')}
              className="group relative p-6 portrait:p-4 bg-gradient-to-br from-blue-50 to-indigo-100 hover:from-blue-100 hover:to-indigo-200 rounded-2xl portrait:rounded-xl border border-blue-200/50 transition-all duration-300 hover:scale-105 hover:shadow-lg"
            >
              <div className="w-12 h-12 portrait:w-10 portrait:h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl portrait:rounded-lg flex items-center justify-center mb-3 portrait:mb-2 group-hover:scale-110 transition-transform duration-300">
                <Upload className="w-6 h-6 portrait:w-5 portrait:h-5 text-white" />
              </div>
              <h3 className="font-semibold text-gray-900 text-sm portrait:text-xs mb-1">提交作业</h3>
              <p className="text-xs portrait:text-[10px] text-gray-600">快速提交新的计算任务</p>
            </button>

            <button 
              onClick={() => navigate('/console#/fileTransfer')}
              className="group relative p-6 portrait:p-4 bg-gradient-to-br from-green-50 to-emerald-100 hover:from-green-100 hover:to-emerald-200 rounded-2xl portrait:rounded-xl border border-green-200/50 transition-all duration-300 hover:scale-105 hover:shadow-lg"
            >
              <div className="w-12 h-12 portrait:w-10 portrait:h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-xl portrait:rounded-lg flex items-center justify-center mb-3 portrait:mb-2 group-hover:scale-110 transition-transform duration-300">
                <Download className="w-6 h-6 portrait:w-5 portrait:h-5 text-white" />
              </div>
              <h3 className="font-semibold text-gray-900 text-sm portrait:text-xs mb-1">文件传输</h3>
              <p className="text-xs portrait:text-[10px] text-gray-600">上传下载数据文件</p>
            </button>

            <button 
              onClick={() => navigate('/console#/appCenter')}
              className="group relative p-6 portrait:p-4 bg-gradient-to-br from-purple-50 to-pink-100 hover:from-purple-100 hover:to-pink-200 rounded-2xl portrait:rounded-xl border border-purple-200/50 transition-all duration-300 hover:scale-105 hover:shadow-lg"
            >
              <div className="w-12 h-12 portrait:w-10 portrait:h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl portrait:rounded-lg flex items-center justify-center mb-3 portrait:mb-2 group-hover:scale-110 transition-transform duration-300">
                <Database className="w-6 h-6 portrait:w-5 portrait:h-5 text-white" />
              </div>
              <h3 className="font-semibold text-gray-900 text-sm portrait:text-xs mb-1">应用中心</h3>
              <p className="text-xs portrait:text-[10px] text-gray-600">浏览可用的应用程序</p>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
